import { useState, useEffect, useCallback, type ReactNode } from "react";
import { motion } from "framer-motion";
import { Link, useParams } from "react-router-dom";
import { FieldType, PINNACLE_FORM_SCHEMA, type FormField } from "@aam/shared";
import { getSessionToken } from "../hooks/useSession";
import { Button } from "./ui/Button";
import { DataTableSkeleton } from "./ui/DataTableSkeleton";
import { PageHeader } from "./ui/PageHeader";
import { LoadingStatus, Skeleton, SkeletonBadge, SkeletonText } from "./ui/Skeleton";

const RAW_API_BASE = (import.meta.env.VITE_API_URL as string | undefined)?.trim();
const API_BASE = RAW_API_BASE
  ? (window.location.protocol === "https:"
      ? RAW_API_BASE.replace(/^http:\/\//i, "https://")
      : RAW_API_BASE)
  : "http://localhost:4000";
const REDACTED_ENCRYPTED_VALUE = "__REDACTED_ENCRYPTED_FIELD__";
const numberFormatter = new Intl.NumberFormat("en-US");

interface Submission {
  id: string;
  status: string;
  imo: string;
  imos: string[];
  carriers: string[];
  createdAt: string;
  user: { email: string };
}

interface Draft {
  id: string;
  token: string;
  email: string | null;
  firstName: string | null;
  lastName: string | null;
  currentStep: number;
  createdAt: string;
  updatedAt: string;
  expiresAt: string;
}

interface QueueStatus {
  queueName: string;
  imo: string;
  jobId: string | null;
  state: string;
  progress?: number | object;
  attemptsMade?: number;
  failedReason?: string;
  processedOn?: string | null;
  finishedOn?: string | null;
  returnvalue?: unknown;
  error?: string;
}

interface SubmissionDetail extends Submission {
  formData: Record<string, unknown>;
  encryptedFields: string[];
  jobId: string | null;
  updatedAt: string;
  editable: boolean;
  queue: QueueStatus;
  attempts: SubmissionAttempt[];
  canGenerateW9?: boolean;
  canGenerateEft?: boolean;
}

interface SubmissionAttempt {
  id: string;
  jobId: string | null;
  status: string;
  reason: string | null;
  queuedAt: string;
  startedAt: string | null;
  finishedAt: string | null;
  createdAt: string;
  updatedAt: string;
}

interface Artifact {
  fileName: string;
  size: number;
  createdAt: string;
  updatedAt: string;
}

interface ArtifactRun {
  runId: string;
  artifacts: Artifact[];
  account?: {
    email: string;
    username: string;
    password: string;
    inviteUrl?: string | null;
    createdAt: string;
  } | null;
}

interface Pagination {
  total: number;
  page: number;
  pageSize: number;
  totalPages: number;
}

const STATUS_COLORS: Record<string, string> = {
  queued: "border border-border-default bg-surface-overlay text-content-secondary",
  waiting: "border border-border-default bg-surface-overlay text-content-secondary",
  delayed: "border border-status-pending-border bg-status-pending-bg text-status-pending-text",
  active: "border border-accent/30 bg-accent/10 text-accent",
  processing: "border border-accent/30 bg-accent/10 text-accent",
  completed: "border border-status-active-border bg-status-active-bg text-status-active-text",
  done: "border border-status-active-border bg-status-active-bg text-status-active-text",
  fulfilled: "border border-status-active-border bg-status-active-bg text-status-active-text",
  failed: "border border-status-rejected-border bg-status-rejected-bg text-status-rejected-text",
  unavailable: "border border-status-rejected-border bg-status-rejected-bg text-status-rejected-text",
};

const IMO_COLORS: Record<string, string> = {
  PINNACLE: "border border-accent-muted bg-accent-muted text-content-primary",
  PREMIER_PRODUCERS_GROUP: "border border-status-pending-border bg-status-pending-bg text-status-pending-text",
  HEARTLAND: "border border-status-nigo-border bg-status-nigo-bg text-status-nigo-text",
  SMS: "border border-status-active-border bg-status-active-bg text-status-active-text",
};

const FORM_FIELD_LABELS = new Map(
  PINNACLE_FORM_SCHEMA.sections.flatMap((section) =>
    section.fields.map((field) => [field.id, field.label] as const)
  )
);
const FORM_FIELDS_BY_ID = new Map(
  PINNACLE_FORM_SCHEMA.sections.flatMap((section) =>
    section.fields.map((field) => [field.id, field] as const)
  )
);
const OPTION_LABELS = new Map(
  PINNACLE_FORM_SCHEMA.sections.flatMap((section) =>
    section.fields.flatMap((field) =>
      (field.options ?? []).map((option) => [option.value, option.label] as const)
    )
  )
);

export function Dashboard() {
  const { submissionId } = useParams<{ submissionId?: string }>();

  return submissionId ? (
    <SubmissionDetailView submissionId={submissionId} />
  ) : (
    <SubmissionList />
  );
}

function fieldLabel(key: string) {
  return FORM_FIELD_LABELS.get(key) ??
    key
      .replace(/([a-z])([A-Z])/g, "$1 $2")
      .replace(/[_-]/g, " ")
      .replace(/\b\w/g, (char) => char.toUpperCase());
}

function displayLabel(value: string) {
  return OPTION_LABELS.get(value) ?? value.replace(/_/g, " ");
}

function PillList({ items }: { items: string[] }) {
  if (items.length === 0) return <span className="text-content-muted">None</span>;

  return (
    <div className="flex flex-wrap gap-1.5">
      {items.map((item) => (
        <span
          key={item}
          className="rounded-full border border-border-default bg-surface-overlay px-2.5 py-1 text-xs text-content-secondary"
        >
          {displayLabel(item)}
        </span>
      ))}
    </div>
  );
}

function formatPhoneValue(value: unknown) {
  const cleaned = String(value ?? "").replace(/\D/g, "");
  const digits = cleaned.length === 11 && cleaned.startsWith("1")
    ? cleaned.slice(1)
    : cleaned;

  if (digits.length !== 10) return String(value ?? "");
  return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6)}`;
}

function formatBytes(bytes: number) {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

function phoneHref(value: unknown) {
  const cleaned = String(value ?? "").replace(/\D/g, "");
  const digits = cleaned.length === 11 && cleaned.startsWith("1")
    ? cleaned.slice(1)
    : cleaned;

  return digits.length === 10 ? `tel:+1${digits}` : `tel:${cleaned}`;
}

function EmailLink({ email }: { email: string }) {
  return (
    <a href={`mailto:${email}`} className="focus-ring rounded text-accent hover:underline">
      {email}
    </a>
  );
}

function PhoneLink({ value }: { value: unknown }) {
  return (
    <a href={phoneHref(value)} className="focus-ring rounded text-accent hover:underline">
      {formatPhoneValue(value)}
    </a>
  );
}

function formatValue(value: unknown): ReactNode {
  if (value === null || value === undefined || value === "") {
    return <span className="text-content-muted">Not provided</span>;
  }
  if (typeof value === "boolean") return value ? "Yes" : "No";
  if (Array.isArray(value)) {
    return <PillList items={value.map(String)} />;
  }
  if (typeof value === "object") {
    return (
      <pre className="whitespace-pre-wrap break-words font-mono text-xs leading-relaxed text-content-secondary">
        {JSON.stringify(value, null, 2)}
      </pre>
    );
  }
  const text = String(value);
  return text.length > 500 ? `${text.slice(0, 500)}...` : text;
}

function formatFieldValue(key: string, value: unknown): ReactNode {
  const field = FORM_FIELDS_BY_ID.get(key) as FormField | undefined;

  if (field?.encrypted || value === REDACTED_ENCRYPTED_VALUE) {
    return (
      <span className="inline-flex items-center rounded-full border border-status-pending-border bg-status-pending-bg px-2.5 py-1 text-xs text-status-pending-text">
        Encrypted field hidden
      </span>
    );
  }

  if (field?.type === FieldType.SIGNATURE && typeof value === "string") {
    if (!value) return <span className="text-content-muted">Not provided</span>;
    if (value.startsWith("data:image/")) {
      return (
        <div className="inline-block rounded-lg border border-border-default bg-surface-overlay p-3">
          <img src={value} alt="Signature" className="max-h-28 max-w-sm object-contain" />
        </div>
      );
    }
  }

  if (field?.type === FieldType.CHECKBOX) {
    return (
      <span className={`rounded-full px-2.5 py-1 text-xs ${
        value
          ? "border border-status-active-border bg-status-active-bg text-status-active-text"
          : "border border-border-default bg-surface-overlay text-content-secondary"
      }`}>
        {value ? "Yes" : "No"}
      </span>
    );
  }

  if (field?.type === FieldType.PHONE) {
    return <PhoneLink value={value} />;
  }

  if (field?.type === FieldType.EMAIL && typeof value === "string") {
    return <EmailLink email={value} />;
  }

  if (Array.isArray(value)) return <PillList items={value.map(String)} />;

  if (typeof value === "string" && field?.options?.length) {
    return <PillList items={[value]} />;
  }

  return formatValue(value);
}

function editableValue(value: unknown) {
  if (Array.isArray(value)) return value.map(String).join(", ");
  if (typeof value === "boolean") return value ? "true" : "false";
  if (value === null || value === undefined) return "";
  if (typeof value === "object") return JSON.stringify(value, null, 2);
  return String(value);
}

function parseEditableValue(field: FormField | undefined, raw: string): unknown {
  if (field?.type === FieldType.CHECKBOX) return raw === "true";
  if (field?.type === FieldType.CHECKBOX_GROUP || raw.includes(",")) {
    return raw
      .split(",")
      .map((item) => item.trim())
      .filter(Boolean);
  }
  return raw;
}

function MetadataItem({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div className="rounded-lg border border-border-subtle bg-surface-overlay px-3 py-2">
      <p className="mb-1 text-xs font-medium uppercase tracking-wide text-content-muted">{label}</p>
      <div className="break-words text-sm text-content-primary">{children}</div>
    </div>
  );
}

function CollapsibleCard({
  title,
  subtitle,
  open,
  onToggle,
  trailing,
  children,
}: {
  title: string;
  subtitle?: string;
  open: boolean;
  onToggle: () => void;
  trailing?: ReactNode;
  children: ReactNode;
}) {
  return (
    <section className="rounded-[var(--card-radius)] border border-border-default bg-surface-raised shadow-card">
      <button
        type="button"
        onClick={onToggle}
        className="focus-ring flex w-full items-center justify-between gap-4 rounded-[var(--card-radius)] px-4 py-3 text-left"
        aria-expanded={open}
      >
        <div>
          <h2 className="text-lg font-semibold text-content-primary">{title}</h2>
          {subtitle && <p className="text-xs text-content-muted">{subtitle}</p>}
        </div>
        <div className="flex items-center gap-3">
          {trailing}
          <span className="text-content-muted" aria-hidden="true">{open ? "−" : "+"}</span>
        </div>
      </button>
      {open && <div className="border-t border-border-subtle p-4">{children}</div>}
    </section>
  );
}

function SubmissionArtifacts({ submissionId }: { submissionId: string }) {
  const [runs, setRuns] = useState<ArtifactRun[]>([]);
  const [images, setImages] = useState<Record<string, string>>({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    const token = getSessionToken();
    if (!token) {
      setError("You need to log in before viewing artifacts.");
      setLoading(false);
      return;
    }

    let cancelled = false;
    const objectUrls: string[] = [];

    async function fetchArtifactImage(runId: string, fileName: string) {
      const response = await fetch(
        `${API_BASE}/api/reagan/artifacts/${encodeURIComponent(runId)}/${encodeURIComponent(fileName)}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      if (!response.ok) throw new Error(`Failed to load ${fileName}`);
      const objectUrl = URL.createObjectURL(await response.blob());
      objectUrls.push(objectUrl);
      return objectUrl;
    }

    async function loadArtifacts() {
      setLoading(true);
      setError("");

      try {
        const response = await fetch(`${API_BASE}/api/reagan/artifacts`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (!response.ok) throw new Error(`Artifact list failed with ${response.status}`);

        const json = await response.json();
        const matchingRuns = ((json.data ?? []) as ArtifactRun[])
          .filter((run) => run.runId.startsWith(`${submissionId}-`))
          .sort((a, b) => b.runId.localeCompare(a.runId));

        if (cancelled) return;
        setRuns(matchingRuns);

        const nextImages: Record<string, string> = {};
        for (const run of matchingRuns) {
          for (const artifact of run.artifacts) {
            const key = `${run.runId}/${artifact.fileName}`;
            nextImages[key] = await fetchArtifactImage(run.runId, artifact.fileName);
            if (!cancelled) setImages({ ...nextImages });
          }
        }
      } catch (err) {
        if (!cancelled) {
          setError(err instanceof Error ? err.message : "Unable to load artifacts.");
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    }

    loadArtifacts();

    return () => {
      cancelled = true;
      objectUrls.forEach((url) => URL.revokeObjectURL(url));
    };
  }, [submissionId]);

  if (loading) {
    return (
      <div role="status" aria-busy="true" className="grid gap-4 md:grid-cols-2">
        <LoadingStatus label="Loading ReaganAI artifacts" />
        <Skeleton className="aspect-video w-full rounded-lg" />
        <Skeleton className="aspect-video w-full rounded-lg" />
      </div>
    );
  }
  if (error) {
    return <p role="alert" className="rounded-lg border border-status-rejected-border bg-status-rejected-bg p-3 text-sm text-status-rejected-text">{error}</p>;
  }
  if (runs.length === 0) {
    return <p className="rounded-lg border border-dashed border-border-default p-5 text-sm text-content-muted">No ReaganAI artifacts found for this submission yet.</p>;
  }

  return (
    <div className="space-y-5">
      {runs.map((run) => (
        <div key={run.runId} className="rounded-xl border border-border-default bg-surface-overlay p-3">
          <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
            <p className="font-mono text-xs text-content-muted">{run.runId}</p>
            <Link to="/run-artifacts" className="text-xs text-accent hover:underline">
              View all artifacts
            </Link>
          </div>
          {run.account && (
            <div className="mb-4 rounded-xl border border-status-active-border bg-status-active-bg p-3">
              <p className="mb-2 text-xs font-semibold text-status-active-text">Generated ReaganAI credentials</p>
              <div className="grid gap-2 md:grid-cols-3">
                <div>
                  <p className="text-[11px] text-status-active-text">Email</p>
                  <p className="break-all font-mono text-xs text-content-primary">{run.account.email}</p>
                </div>
                <div>
                  <p className="text-[11px] text-status-active-text">Username</p>
                  <p className="break-all font-mono text-xs text-content-primary">{run.account.username}</p>
                </div>
                <div>
                  <p className="text-[11px] text-status-active-text">Password</p>
                  <p className="break-all font-mono text-xs text-content-primary">{run.account.password}</p>
                </div>
              </div>
              {run.account.inviteUrl && (
                <a
                  href={run.account.inviteUrl}
                  target="_blank"
                  rel="noreferrer"
                  className="focus-ring mt-2 inline-block rounded text-[11px] text-status-active-text underline"
                >
                  Open invite link
                </a>
              )}
            </div>
          )}
          <div className="grid gap-4 md:grid-cols-2">
            {run.artifacts.map((artifact) => {
              const key = `${run.runId}/${artifact.fileName}`;
              return (
                <figure key={key} className="overflow-hidden rounded-lg border border-border-default bg-surface-base">
                  <div className="aspect-video bg-surface-overlay flex items-center justify-center">
                    {images[key] ? (
                      <img
                        src={images[key]}
                        alt={`${run.runId} ${artifact.fileName}`}
                        className="h-full w-full object-contain"
                      />
                    ) : (
                      <span role="status" className="text-xs text-content-muted">Loading image…</span>
                    )}
                  </div>
                  <figcaption className="border-t border-border-default p-3">
                    <p className="break-all font-mono text-xs text-content-primary">{artifact.fileName}</p>
                    <p className="mt-1 text-xs text-content-muted">
                      {formatBytes(artifact.size)} · {new Date(artifact.updatedAt).toLocaleString()}
                    </p>
                  </figcaption>
                </figure>
              );
            })}
          </div>
        </div>
      ))}
    </div>
  );
}


function SubmissionList() {
  const [activeTab, setActiveTab] = useState<"submissions" | "drafts">("submissions");
  const [submissions, setSubmissions] = useState<Submission[]>([]);
  const [drafts, setDrafts] = useState<Draft[]>([]);
  const [pagination, setPagination] = useState<Pagination>({
    total: 0,
    page: 1,
    pageSize: 25,
    totalPages: 1,
  });
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const fetchSubmissions = useCallback(async () => {
    try {
      const token = getSessionToken();
      const params = new URLSearchParams({
        page: pagination.page.toString(),
        pageSize: pagination.pageSize.toString(),
      });
      if (search.trim()) params.set("email", search.trim());

      const res = await fetch(`${API_BASE}/api/submissions?${params.toString()}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) throw new Error("Failed to load submissions");
      const json = await res.json();
      const data = Array.isArray(json.data)
        ? {
            items: json.data,
            pagination: {
              total: json.data.length,
              page: 1,
              pageSize: json.data.length || pagination.pageSize,
              totalPages: 1,
            },
          }
        : json.data;
      setSubmissions(data.items);
      setPagination(data.pagination);
      setError("");
    } catch (err) {
      setError(err instanceof Error ? err.message : "Unknown error");
    } finally {
      setLoading(false);
    }
  }, [search, pagination.page, pagination.pageSize]);

  const fetchDrafts = useCallback(async () => {
    try {
      const token = getSessionToken();
      const params = new URLSearchParams({
        page: pagination.page.toString(),
        pageSize: pagination.pageSize.toString(),
      });
      if (search.trim()) params.set("search", search.trim());

      const res = await fetch(`${API_BASE}/api/drafts?${params.toString()}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) throw new Error("Failed to load drafts");
      const json = await res.json();
      const data = Array.isArray(json.data)
        ? {
            items: json.data,
            pagination: {
              total: json.data.length,
              page: 1,
              pageSize: json.data.length || pagination.pageSize,
              totalPages: 1,
            },
          }
        : json.data;
      setDrafts(data.items);
      setPagination(data.pagination);
      setError("");
    } catch (err) {
      setError(err instanceof Error ? err.message : "Unknown error");
    } finally {
      setLoading(false);
    }
  }, [search, pagination.page, pagination.pageSize]);

  const fetchData = useCallback(() => {
    if (activeTab === "submissions") {
      fetchSubmissions();
    } else {
      fetchDrafts();
    }
  }, [activeTab, fetchSubmissions, fetchDrafts]);

  useEffect(() => {
    setLoading(true);
    fetchData();
    const interval = setInterval(fetchData, 10000);
    return () => clearInterval(interval);
  }, [fetchData]);

  const totalLabel = activeTab === "submissions"
    ? `${numberFormatter.format(pagination.total)} ${pagination.total === 1 ? "submission" : "submissions"}`
    : `${numberFormatter.format(pagination.total)} ${pagination.total === 1 ? "draft" : "drafts"}`;

  const selectTab = (tab: "submissions" | "drafts") => {
    setActiveTab(tab);
    setPagination((current) => ({ ...current, page: 1 }));
  };

  const handleTabKey = (event: React.KeyboardEvent<HTMLButtonElement>) => {
    if (!["ArrowLeft", "ArrowRight", "Home", "End"].includes(event.key)) return;
    event.preventDefault();
    const tab = event.key === "ArrowLeft" || event.key === "Home" ? "submissions" : "drafts";
    selectTab(tab);
    document.getElementById(`operations-tab-${tab}`)?.focus();
  };

  return (
    <section className="space-y-5">
      <PageHeader
        title="Submission operations"
        description="Monitor submitted applications, active drafts, and processing status."
        actions={<Button size="sm" variant="secondary" onClick={fetchData}>Refresh</Button>}
      />

      <div className="flex flex-wrap items-center justify-between gap-3 border-b border-border-subtle">
        <div className="flex items-center gap-1" role="tablist" aria-label="Application type">
            <button
              id="operations-tab-submissions"
              type="button"
              role="tab"
              aria-selected={activeTab === "submissions"}
              aria-controls="operations-tabpanel"
              tabIndex={activeTab === "submissions" ? 0 : -1}
              onKeyDown={handleTabKey}
              onClick={() => selectTab("submissions")}
              className={`focus-ring border-b-2 px-4 py-3 text-sm font-semibold transition-colors ${
                activeTab === "submissions"
                  ? "border-accent text-content-primary"
                  : "border-transparent text-content-muted hover:text-content-primary"
              }`}
            >
              Submissions
            </button>
            <button
              id="operations-tab-drafts"
              type="button"
              role="tab"
              aria-selected={activeTab === "drafts"}
              aria-controls="operations-tabpanel"
              tabIndex={activeTab === "drafts" ? 0 : -1}
              onKeyDown={handleTabKey}
              onClick={() => selectTab("drafts")}
              className={`focus-ring border-b-2 px-4 py-3 text-sm font-semibold transition-colors ${
                activeTab === "drafts"
                  ? "border-accent text-content-primary"
                  : "border-transparent text-content-muted hover:text-content-primary"
              }`}
            >
              In-progress applications
            </button>
        </div>
        <p className="pb-3 text-sm font-medium tabular-nums text-content-secondary">{totalLabel}</p>
      </div>

      <div id="operations-tabpanel" role="tabpanel" aria-labelledby={`operations-tab-${activeTab}`} className="contents">
      <div className="grid gap-3 rounded-[var(--card-radius)] border border-border-default bg-surface-raised p-4 shadow-card md:grid-cols-[1fr_160px]">
        <label className="block">
          <span className="mb-1.5 block text-xs font-medium text-content-secondary">
            {activeTab === "submissions" ? "Search email" : "Search name or email"}
          </span>
          <input
            type="search"
            value={search}
            onChange={(event) => {
              setSearch(event.target.value);
              setPagination((current) => ({ ...current, page: 1 }));
            }}
            placeholder={activeTab === "submissions" ? "agent@example.com" : "First name, last name, or email"}
            className="focus-ring w-full rounded-lg border border-border-default bg-surface-overlay px-3 py-2 text-sm text-content-primary placeholder:text-content-muted"
          />
        </label>
        <label className="block min-w-36">
          <span className="mb-1.5 block text-xs font-medium text-content-secondary">Per page</span>
          <select
            value={pagination.pageSize}
            onChange={(event) => {
              setPagination((current) => ({
                ...current,
                page: 1,
                pageSize: Number(event.target.value),
              }));
            }}
            className="focus-ring w-full rounded-lg border border-border-default bg-surface-overlay px-3 py-2 text-sm text-content-primary"
          >
            {[10, 25, 50, 100].map((size) => (
              <option key={size} value={size}>
                {size}
              </option>
            ))}
          </select>
        </label>
      </div>

      {loading && <DataTableSkeleton headers={activeTab === "submissions" ? ["Agent", "Status", "Organization", "Carriers", "Submitted"] : ["Applicant", "Progress", "Updated", "Expires", "Action"]} cellVariants={activeTab === "submissions" ? ["multiline", "badge", "text", "pills", "text"] : ["multiline", "text", "text", "text", "short"]} rows={7} loadingLabel={`Loading ${activeTab}`} />}

      {error && (
        <div role="alert" className="rounded-lg border border-status-rejected-border bg-status-rejected-bg p-4 text-sm text-status-rejected-text">
          {error}
        </div>
      )}

      {!loading && !error && activeTab === "submissions" && submissions.length === 0 && (
        <div className="rounded-[var(--card-radius)] border border-dashed border-border-default bg-surface-raised py-14 text-center">
          <p className="font-medium text-content-primary">No submissions yet</p>
          <p className="mt-1 text-sm text-content-muted">Completed applications will appear here.</p>
        </div>
      )}

      {!loading && !error && activeTab === "drafts" && drafts.length === 0 && (
        <div className="rounded-[var(--card-radius)] border border-dashed border-border-default bg-surface-raised py-14 text-center">
          <p className="font-medium text-content-primary">No in-progress applications</p>
          <p className="mt-1 text-sm text-content-muted">Saved drafts will appear here.</p>
        </div>
      )}

      {!loading && activeTab === "submissions" && submissions.length > 0 && (
        <div className="overflow-hidden rounded-[var(--card-radius)] border border-border-default bg-surface-raised shadow-card">
          {submissions.map((sub, i) => (
            <Link key={sub.id} to={`/dashboard/submissions/${sub.id}`} className="block">
              <motion.div
                initial={{ opacity: 0, y: 8 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: i * 0.03 }}
                className="border-b border-border-subtle px-5 py-4 transition-colors last:border-b-0 hover:bg-surface-overlay"
              >
                <div className="flex items-start justify-between gap-3 flex-wrap">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap mb-1.5">
                      <span className={`text-xs px-2 py-0.5 rounded-full font-medium capitalize ${STATUS_COLORS[sub.status] ?? "bg-surface-overlay text-content-secondary"}`}>
                        {sub.status}
                      </span>
                      <span className={`text-xs px-2 py-0.5 rounded-full ${IMO_COLORS[sub.imo] ?? "bg-surface-overlay text-content-secondary"}`}>
                        {sub.imo.replace(/_/g, " ")}
                      </span>
                    </div>
                    <p className="truncate text-sm font-semibold text-content-primary">{sub.user?.email}</p>
                    <div className="mt-1">
                      <PillList items={sub.carriers.slice(0, 4)} />
                      {sub.carriers.length > 4 && (
                        <p className="mt-1 text-xs text-content-muted">
                          +{sub.carriers.length - 4} more carriers
                        </p>
                      )}
                    </div>
                  </div>
                  <div className="text-right shrink-0">
                    <p className="text-xs text-content-muted">
                      {new Date(sub.createdAt).toLocaleDateString("en-US", {
                        month: "short", day: "numeric", year: "numeric",
                      })}
                    </p>
                    <p className="mt-0.5 font-mono text-xs text-content-muted">
                      {sub.id.slice(0, 8)}…
                    </p>
                    <p className="mt-1 text-xs font-medium text-accent">View details →</p>
                  </div>
                </div>
              </motion.div>
            </Link>
          ))}
        </div>
      )}

      {!loading && activeTab === "drafts" && drafts.length > 0 && (
        <div className="overflow-hidden rounded-[var(--card-radius)] border border-border-default bg-surface-raised shadow-card">
          {drafts.map((draft, i) => {
            const displayName = [draft.firstName, draft.lastName].filter(Boolean).join(" ") || draft.email || "Unknown";
            const expiresDate = new Date(draft.expiresAt);
            const isExpiringSoon = (expiresDate.getTime() - Date.now()) < 7 * 24 * 60 * 60 * 1000;
            
            return (
              <a key={draft.id} href={`/agent-form/${draft.token}/agent-info`} className="block" target="_blank" rel="noreferrer">
                <motion.div
                  initial={{ opacity: 0, y: 8 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: i * 0.03 }}
                  className="border-b border-border-subtle px-5 py-4 transition-colors last:border-b-0 hover:bg-surface-overlay"
                >
                  <div className="flex items-start justify-between gap-3 flex-wrap">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap mb-1.5">
                        <span className="rounded-full border border-status-nigo-border bg-status-nigo-bg px-2 py-0.5 text-xs text-status-nigo-text">
                          In progress
                        </span>
                        {isExpiringSoon && (
                          <span className="rounded-full border border-status-rejected-border bg-status-rejected-bg px-2 py-0.5 text-xs text-status-rejected-text">
                            Expires soon
                          </span>
                        )}
                      </div>
                      <p className="text-sm font-semibold text-content-primary">{displayName}</p>
                      {draft.email && (
                        <p className="mt-0.5 text-xs text-content-secondary">{draft.email}</p>
                      )}
                      <p className="mt-1 text-xs text-content-muted">
                        Step {draft.currentStep + 1} · Last updated {new Date(draft.updatedAt).toLocaleDateString("en-US", {
                          month: "short", day: "numeric", year: "numeric",
                        })}
                      </p>
                    </div>
                    <div className="text-right shrink-0">
                      <p className="text-xs text-content-muted">
                        Created {new Date(draft.createdAt).toLocaleDateString("en-US", {
                          month: "short", day: "numeric", year: "numeric",
                        })}
                      </p>
                      <p className="mt-0.5 font-mono text-xs text-content-muted">
                        {draft.token.slice(0, 8)}…
                      </p>
                      <p className="mt-1 text-xs font-medium text-accent">Resume application →</p>
                    </div>
                  </div>
                </motion.div>
              </a>
            );
          })}
        </div>
      )}

      {!loading && !error && (
        <div className="flex items-center justify-between gap-3 rounded-lg border border-border-default bg-surface-raised px-4 py-3 text-sm text-content-secondary">
          <span>
            Page {pagination.page} of {pagination.totalPages}
          </span>
          <div className="flex items-center gap-2">
            <Button
              size="sm"
              variant="secondary"
              disabled={pagination.page <= 1}
              onClick={() => setPagination((current) => ({ ...current, page: Math.max(1, current.page - 1) }))}
            >
              Previous
            </Button>
            <Button
              size="sm"
              variant="secondary"
              disabled={pagination.page >= pagination.totalPages}
              onClick={() => setPagination((current) => ({ ...current, page: Math.min(current.totalPages, current.page + 1) }))}
            >
              Next
            </Button>
          </div>
        </div>
      )}
      </div>

      <div className="text-center">
        <a
          href="/"
          className="text-xs text-content-muted transition-colors hover:text-content-primary"
        >
          ← Back to registration form
        </a>
      </div>
    </section>
  );
}

function SubmissionDetailView({ submissionId }: { submissionId: string }) {
  const [submission, setSubmission] = useState<SubmissionDetail | null>(null);
  const [editableFormData, setEditableFormData] = useState<Record<string, unknown>>({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [actionError, setActionError] = useState("");
  const [saving, setSaving] = useState(false);
  const [retrying, setRetrying] = useState(false);
  const [submissionOpen, setSubmissionOpen] = useState(true);
  const [documentsOpen, setDocumentsOpen] = useState(true);
  const [formOpen, setFormOpen] = useState(true);
  const [artifactsOpen, setArtifactsOpen] = useState(true);
  const [queueOpen, setQueueOpen] = useState(true);

  const fetchSubmission = useCallback(async () => {
    try {
      const token = getSessionToken();
      const res = await fetch(`${API_BASE}/api/submissions/${submissionId}/detail`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) throw new Error("Failed to load submission details");
      const json = await res.json();
      setSubmission(json.data);
      setEditableFormData(json.data.formData ?? {});
      setError("");
    } catch (err) {
      setError(err instanceof Error ? err.message : "Unknown error");
    } finally {
      setLoading(false);
    }
  }, [submissionId]);

  useEffect(() => {
    fetchSubmission();
    if (submission?.editable) return;

    const interval = setInterval(fetchSubmission, 10000);
    return () => clearInterval(interval);
  }, [fetchSubmission, submission?.editable]);

  const updateEditableValue = (key: string, value: unknown) => {
    setEditableFormData((current) => ({ ...current, [key]: value }));
  };

  const saveFormEdits = async () => {
    if (!submission?.editable) return;

    setSaving(true);
    setActionError("");
    try {
      const token = getSessionToken();
      const carriers = Array.isArray(editableFormData.selectedCarriers)
        ? editableFormData.selectedCarriers.map(String)
        : undefined;
      const res = await fetch(`${API_BASE}/api/submissions/${submissionId}/form`, {
        method: "PATCH",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ formData: editableFormData, carriers }),
      });
      const json = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(json.error || "Failed to save form edits");
      setSubmission(json.data);
      setEditableFormData(json.data.formData ?? {});
    } catch (err) {
      setActionError(err instanceof Error ? err.message : "Unknown error");
    } finally {
      setSaving(false);
    }
  };

  const downloadGeneratedFile = async (type: "w9" | "eft") => {
    setActionError("");
    try {
      const token = getSessionToken();
      const res = await fetch(`${API_BASE}/api/forms/download/${submissionId}/${type}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) {
        const json = (await res.json().catch(() => null)) as { error?: string } | null;
        throw new Error(json?.error || `Failed to download ${type.toUpperCase()} file`);
      }
      const blob = await res.blob();
      const objectUrl = URL.createObjectURL(blob);
      const anchor = document.createElement("a");
      anchor.href = objectUrl;
      anchor.download = type === "w9" ? "W9-Form-Filled.pdf" : "EFT-Form-Filled.pdf";
      document.body.appendChild(anchor);
      anchor.click();
      document.body.removeChild(anchor);
      URL.revokeObjectURL(objectUrl);
    } catch (err) {
      setActionError(err instanceof Error ? err.message : "Unable to download file.");
    }
  };

  const retrySubmission = async () => {
    if (!submission?.editable) return;

    setRetrying(true);
    setActionError("");
    try {
      const token = getSessionToken();
      const res = await fetch(`${API_BASE}/api/submissions/${submissionId}/retry`, {
        method: "POST",
        headers: { Authorization: `Bearer ${token}` },
      });
      const json = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(json.error || "Failed to requeue submission");
      await fetchSubmission();
    } catch (err) {
      setActionError(err instanceof Error ? err.message : "Unknown error");
    } finally {
      setRetrying(false);
    }
  };

  if (loading) {
    return (
      <div role="status" aria-busy="true" className="space-y-6">
        <LoadingStatus label="Loading submission details" />
        <div className="flex items-start justify-between gap-4">
          <div className="space-y-3">
            <SkeletonText width="w-28" />
            <Skeleton className="h-8 w-64" />
            <SkeletonText width="w-72" />
          </div>
          <div className="flex gap-2">
            <Skeleton className="h-11 w-28" />
            <Skeleton className="h-11 w-24" />
          </div>
        </div>
        {Array.from({ length: 4 }).map((_, index) => (
          <div key={index} className="rounded-[var(--card-radius)] border border-border-default bg-surface-raised p-4 shadow-card">
            <div className="mb-4 flex items-center justify-between">
              <Skeleton className="h-6 w-36" />
              <SkeletonBadge />
            </div>
            <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
              {Array.from({ length: 6 }).map((__, itemIndex) => (
                <Skeleton key={itemIndex} className="h-16 w-full" />
              ))}
            </div>
          </div>
        ))}
      </div>
    );
  }

  if (error || !submission) {
    return (
      <div className="space-y-4">
        <Link to="/dashboard/agents" className="focus-ring inline-flex rounded text-sm text-content-muted hover:text-content-primary">← Back to agents</Link>
        <div role="alert" className="rounded-xl border border-status-rejected-border bg-status-rejected-bg p-4 text-sm text-status-rejected-text">
          {error || "Submission not found"}
        </div>
      </div>
    );
  }

  const formEntries = Object.entries(submission.formData ?? {});
  const firstName = typeof submission.formData.firstName === "string" ? submission.formData.firstName : null;
  const lastName = typeof submission.formData.lastName === "string" ? submission.formData.lastName : null;
  const displayName = [firstName, lastName].filter(Boolean).join(" ");

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-start justify-between gap-4 border-b border-border-subtle pb-5">
        <div>
          <Link to="/dashboard/agents" className="focus-ring inline-flex rounded text-sm text-content-muted hover:text-content-primary">← Back to agents</Link>
          <h1 className="mt-3 font-display text-2xl font-bold text-content-primary">
            {displayName || "Submission detail"}
          </h1>
          <p className="mt-1 break-all font-mono text-sm text-content-muted">{submission.id}</p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          {submission.editable && (
            <>
              <Button
                size="sm"
                onClick={saveFormEdits}
                disabled={saving || retrying}
              >
                {saving ? "Saving..." : "Save changes"}
              </Button>
              <Button
                size="sm"
                variant="secondary"
                onClick={retrySubmission}
                disabled={saving || retrying}
              >
                {retrying ? "Requeueing..." : "Retry / requeue"}
              </Button>
            </>
          )}
          <Button
            size="sm"
            variant="ghost"
            onClick={fetchSubmission}
          >
            Refresh
          </Button>
        </div>
      </div>

      {actionError && (
        <div role="alert" className="rounded-xl border border-status-rejected-border bg-status-rejected-bg p-3 text-sm text-status-rejected-text">
          {actionError}
        </div>
      )}

      {!submission.editable && (
        <div className="rounded-xl border border-status-pending-border bg-status-pending-bg p-3 text-sm text-status-pending-text">
          Form edits and retries are only available once a submission is failed after BullMQ exhausts retries.
        </div>
      )}

      <CollapsibleCard
        title="Submission"
        open={submissionOpen}
        onToggle={() => setSubmissionOpen((open) => !open)}
      >
        <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
          <MetadataItem label="Status">
            <span className={`rounded-full px-2 py-0.5 text-xs font-medium capitalize ${STATUS_COLORS[submission.status] ?? "border border-border-default bg-surface-overlay text-content-secondary"}`}>
              {submission.status}
            </span>
          </MetadataItem>
          <MetadataItem label="Primary IMO">
            <span className={`rounded-full px-2 py-0.5 text-xs ${IMO_COLORS[submission.imo] ?? "border border-border-default bg-surface-overlay text-content-secondary"}`}>
              {submission.imo.replace(/_/g, " ")}
            </span>
          </MetadataItem>
          <MetadataItem label="All IMOs">
            <PillList items={submission.imos ?? [submission.imo]} />
          </MetadataItem>
          <MetadataItem label="Agent email">
            {submission.user?.email ? <EmailLink email={submission.user.email} /> : "Not provided"}
          </MetadataItem>
          <MetadataItem label="Submission ID">
            <span className="font-mono text-xs">{submission.id}</span>
          </MetadataItem>
          <MetadataItem label="Created">{new Date(submission.createdAt).toLocaleString()}</MetadataItem>
          <MetadataItem label="Updated">{new Date(submission.updatedAt).toLocaleString()}</MetadataItem>
          <MetadataItem label="Carriers"><PillList items={submission.carriers} /></MetadataItem>
          <MetadataItem label="Encrypted fields"><PillList items={submission.encryptedFields} /></MetadataItem>
        </div>
      </CollapsibleCard>

      <CollapsibleCard
        title="Documents"
        subtitle="W9 and EFT PDFs generated on demand from registration data."
        open={documentsOpen}
        onToggle={() => setDocumentsOpen((open) => !open)}
      >
        {submission.canGenerateW9 || submission.canGenerateEft ? (
          <div className="flex flex-wrap gap-2">
            {submission.canGenerateW9 && (
              <Button
                variant="secondary"
                onClick={() => downloadGeneratedFile("w9")}
              >
                Download W9
              </Button>
            )}
            {submission.canGenerateEft && (
              <Button
                variant="secondary"
                onClick={() => downloadGeneratedFile("eft")}
              >
                Download EFT
              </Button>
            )}
          </div>
        ) : (
          <p className="text-sm text-content-muted">
            This registration didn’t include W9 or EFT details, so there’s nothing to generate.
          </p>
        )}
      </CollapsibleCard>

      <CollapsibleCard
        title="Form data"
        open={formOpen}
        onToggle={() => setFormOpen((open) => !open)}
      >
        {formEntries.length === 0 ? (
          <p className="rounded-lg border border-dashed border-border-default p-5 text-sm text-content-muted">No form data stored for this submission.</p>
        ) : (
          <div className="divide-y divide-border-subtle">
            {formEntries.map(([key, value]) => (
              <div key={key} className="grid gap-2 py-3 md:grid-cols-[240px_1fr]">
                <div>
                  <p className="text-sm font-medium text-content-primary">{fieldLabel(key)}</p>
                  <p className="font-mono text-xs text-content-muted">{key}</p>
                </div>
                <div className="break-words text-sm text-content-secondary">
                  {submission.editable &&
                  !FORM_FIELDS_BY_ID.get(key)?.encrypted &&
                  value !== REDACTED_ENCRYPTED_VALUE &&
                  FORM_FIELDS_BY_ID.get(key)?.type !== FieldType.SIGNATURE ? (
                    FORM_FIELDS_BY_ID.get(key)?.type === FieldType.CHECKBOX ? (
                      <select
                        value={editableValue(editableFormData[key])}
                        onChange={(event) =>
                          updateEditableValue(key, parseEditableValue(FORM_FIELDS_BY_ID.get(key), event.target.value))
                        }
                        className="focus-ring w-full rounded-lg border border-border-default bg-surface-input px-3 py-2 text-sm text-content-primary"
                      >
                        <option value="true">Yes</option>
                        <option value="false">No</option>
                      </select>
                    ) : (
                      <input
                        type={FORM_FIELDS_BY_ID.get(key)?.type === FieldType.DATE ? "date" : "text"}
                        value={editableValue(editableFormData[key])}
                        onChange={(event) =>
                          updateEditableValue(key, parseEditableValue(FORM_FIELDS_BY_ID.get(key), event.target.value))
                        }
                        className="focus-ring w-full rounded-lg border border-border-default bg-surface-input px-3 py-2 text-sm text-content-primary"
                      />
                    )
                  ) : (
                    formatFieldValue(key, value)
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </CollapsibleCard>

      <CollapsibleCard
        title="ReaganAI artifacts"
        subtitle="Screenshots captured for this application."
        open={artifactsOpen}
        onToggle={() => setArtifactsOpen((open) => !open)}
      >
        <SubmissionArtifacts submissionId={submission.id} />
      </CollapsibleCard>

      <CollapsibleCard
        title="Job queue"
        subtitle="Queue details are expanded by default."
        open={queueOpen}
        onToggle={() => setQueueOpen((open) => !open)}
        trailing={
            <span className={`rounded-full px-2 py-0.5 text-xs font-medium capitalize ${STATUS_COLORS[submission.queue.state] ?? "border border-border-default bg-surface-overlay text-content-secondary"}`}>
              {submission.queue.state}
            </span>
        }
      >
            {Array.isArray(submission.imos) && submission.imos.length > 1 && (
              <p className="mb-3 rounded-lg border border-status-pending-border bg-status-pending-bg p-3 text-xs text-status-pending-text">
                This submission spans multiple IMOs. The queue entry below is the primary processor job.
              </p>
            )}
            <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
              <MetadataItem label="Queue">{submission.queue.queueName}</MetadataItem>
              <MetadataItem label="IMO processor">{submission.queue.imo.replace(/_/g, " ")}</MetadataItem>
              <MetadataItem label="Queue state">
                <span className={`rounded-full px-2 py-0.5 text-xs font-medium capitalize ${STATUS_COLORS[submission.queue.state] ?? "border border-border-default bg-surface-overlay text-content-secondary"}`}>
                  {submission.queue.state}
                </span>
              </MetadataItem>
              <MetadataItem label="Job ID">
                <span className="font-mono text-xs">{submission.queue.jobId ?? "None"}</span>
              </MetadataItem>
              <MetadataItem label="Progress">{formatValue(submission.queue.progress ?? "Not started")}</MetadataItem>
              <MetadataItem label="Attempts">{submission.queue.attemptsMade ?? 0}</MetadataItem>
              <MetadataItem label="Processed">{submission.queue.processedOn ? new Date(submission.queue.processedOn).toLocaleString() : "Not processed"}</MetadataItem>
              <MetadataItem label="Finished">{submission.queue.finishedOn ? new Date(submission.queue.finishedOn).toLocaleString() : "Not finished"}</MetadataItem>
              {submission.queue.failedReason && (
                <MetadataItem label="Failure reason">{submission.queue.failedReason}</MetadataItem>
              )}
              {submission.queue.error && (
                <MetadataItem label="Queue read error">{submission.queue.error}</MetadataItem>
              )}
            </div>
            {submission.queue.returnvalue !== undefined && (
              <div className="mt-3 rounded-lg border border-border-subtle bg-surface-overlay px-3 py-2">
                <p className="mb-2 text-xs font-medium uppercase tracking-wide text-content-muted">Job result</p>
                <div className="text-sm text-content-primary">{formatValue(submission.queue.returnvalue)}</div>
              </div>
            )}
            <div className="mt-4">
              <h3 className="mb-2 text-sm font-semibold text-content-primary">Attempt log</h3>
              {submission.attempts.length === 0 ? (
                <p className="rounded-lg border border-dashed border-border-default p-4 text-sm text-content-muted">No recorded attempts yet.</p>
              ) : (
                <div className="space-y-2">
                  {submission.attempts.map((attempt) => (
                    <div key={attempt.id} className="rounded-lg border border-border-subtle bg-surface-overlay p-3">
                      <div className="flex items-start justify-between gap-3">
                        <div>
                          <span className={`rounded-full px-2 py-0.5 text-xs font-medium capitalize ${STATUS_COLORS[attempt.status] ?? "border border-border-default bg-surface-raised text-content-secondary"}`}>
                            {attempt.status}
                          </span>
                          <p className="mt-2 font-mono text-xs text-content-muted">
                            Attempt {attempt.id.slice(0, 8)}
                            {attempt.jobId ? ` | Job ${attempt.jobId}` : ""}
                          </p>
                        </div>
                        <p className="text-right text-xs text-content-muted">
                          Queued {new Date(attempt.queuedAt).toLocaleString()}
                        </p>
                      </div>
                      <div className="mt-2 grid gap-2 text-xs text-content-secondary sm:grid-cols-2">
                        <p>Started: {attempt.startedAt ? new Date(attempt.startedAt).toLocaleString() : "Not started"}</p>
                        <p>Finished: {attempt.finishedAt ? new Date(attempt.finishedAt).toLocaleString() : "Not finished"}</p>
                      </div>
                      {attempt.reason && (
                        <p className="mt-2 rounded-md border border-status-rejected-border bg-status-rejected-bg p-2 text-xs text-status-rejected-text">Reason: {attempt.reason}</p>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>
      </CollapsibleCard>
    </div>
  );
}
